import React from 'react'
import { ThemeProvider, useTheme } from '@mui/material/styles'
import { Box, Typography, Chip, TablePagination } from '@mui/material'
import { Sidescroll, Stack } from 'components'
import intl from 'localization/components'
import MaterialTableOriginal from 'material-table'

// Type assertion to fix React types incompatibility
const MaterialTable: React.FC<Record<string, unknown>> = MaterialTableOriginal as any
import { useState } from 'react'
import { useIntl } from 'react-intl'
import { useQueryClient } from 'react-query'
import { useParams } from 'react-router-dom'
import { countries as Countries } from 'utils/constants'
import AddOrEditZone from './components/add-or-edit-zone'
import DeleteZone from './components/delete-zone'
import { icons, localization } from './global-data-table.model'
import { theme as defaultTheme } from 'styles/theme'

interface Zone {
  name: string
  countries: string[]
  allocation: number
  usage?: number
}

interface GlobalDataTableProps {
  zones: Zone[]
  showTitle?: boolean
}

const GlobalDataTable = ({ zones, showTitle = true }: GlobalDataTableProps) => {
  const currentTheme = useTheme()
  const queryClient = useQueryClient()
  const reactIntl = useIntl()
  const messages = reactIntl.messages as Record<string, string>
  const [isAddOrEditZone, setIsAddOrEditZone] = useState(false)
  const [isDeleteZone, setIsDeleteZone] = useState(false)
  const [editableData, setEditableData] = useState<Zone | undefined>(undefined)
  const [deleteData, setDeleteData] = useState<Zone | undefined>(undefined)
  const { id: selectedCustomer } = useParams()

  const columns = (msgs: Record<string, string>) => {
    return [
      {
        title: msgs[intl.generic('zone-name')],
        field: 'name',
        customFilterAndSearch: (term: string, rowData: Zone) => {
          const formattedCountries = rowData?.countries
            ?.map((country) => country?.toLowerCase())
            ?.join(' ')

          return (
            rowData?.name?.toLowerCase()?.includes(term?.toLowerCase()) ||
            formattedCountries.includes(term?.toLowerCase())
          )
        },
        render: ({ name }: Zone) => {
          return (
            <Typography sx={{ textTransform: 'capitalize', fontSize: 14 }}>{name}</Typography>
          )
        },
      },
      {
        title: msgs[intl.generic('zone-countires')],
        field: 'countries',
        customFilterAndSearch: (term: string, rowData: Zone) => {
          const formattedCountries = rowData?.countries
            ?.map((country) => country?.toLowerCase())
            ?.join(' ')

          return (
            rowData?.name?.toLowerCase()?.includes(term?.toLowerCase()) ||
            formattedCountries.includes(term?.toLowerCase())
          )
        },
        render: (rowData: Zone) => {
          return (
            <Stack
              direction="row"
              spacing={1}
              wrap="wrap"
              style={{ rowGap: '10px' }}
            >
              {rowData.countries.map((country) => {
                const zoneCountry = Countries.getName(country, 'en')
                if (!zoneCountry) {
                  return null
                }
                return (
                  <Chip
                    key={country}
                    size="medium"
                    label={zoneCountry || country}
                    variant="filled"
                    sx={{
                      '& .MuiChip-label': {
                        minWidth: '40px',
                        fontSize: '14px',
                      },
                    }}
                  />
                )
              })}
            </Stack>
          )
        },
      },
      {
        title: msgs[intl.generic('zone-allocations')],
        field: 'allocation',
        width: '10%',
        customFilterAndSearch: (term: string, rowData: Zone) => {
          return String(rowData?.allocation)
            ?.toLowerCase()
            ?.includes(term?.toLowerCase())
        },
      },
      {
        title: msgs[intl.generic('zone-usage')],
        field: 'usage',
        width: '10%',
      },
    ]
  }

  const invalidateQuery = () => {
    queryClient.invalidateQueries(['getCustomer', selectedCustomer])
    queryClient.invalidateQueries(['getCustomerPlan', selectedCustomer])
  }

  const handleDeleteZoneSubscription = () => {
    setIsDeleteZone(false)
    invalidateQuery()
    setDeleteData(undefined)
  }

  const handleAddOrEditZoneSubscription = () => {
    setIsAddOrEditZone(false)
    setEditableData(undefined)
    invalidateQuery()
  }

  const handleCloseAddOrEditZone = () => {
    setIsAddOrEditZone(false)
    setEditableData(undefined)
  }

  return (
    <Sidescroll conditionalySidescroll maxWidth={'819px'}>
      <ThemeProvider theme={currentTheme}>
        <DeleteZone
          zones={zones}
          selectedCustomer={selectedCustomer}
          deleteData={deleteData}
          setOpen={setIsDeleteZone}
          onSubmit={handleDeleteZoneSubscription}
          open={isDeleteZone}
          title={messages[intl.customerZones('zone-delete-title')]}
          content={messages[intl.customerZones('zone-delete-content')]}
        />
        <AddOrEditZone
          customerZones={zones}
          data={editableData}
          setOpen={handleCloseAddOrEditZone}
          onSubmit={handleAddOrEditZoneSubscription}
          open={isAddOrEditZone}
          title={`${editableData ? 'Edit' : 'Add'} Zone`}
        />
        <MaterialTable
          options={{
            pageSize: 20,
            addRowPosition: 'first',
            pageSizeOptions: [20, 50, 100],
            cellStyle: {
              fontSize: '14px',
              color: currentTheme.palette.text.primary,
            },
            headerStyle: {
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'uppercase' as const,
              letterSpacing: '0.5px',
              color: currentTheme.palette.text.secondary,
              backgroundColor: currentTheme.palette.mode === 'dark'
                ? currentTheme.palette.background.default
                : currentTheme.palette.grey[50],
            },
            rowStyle: {
              backgroundColor: currentTheme.palette.background.paper,
            },
          } as any}
          components={{
            Container: (props: any) => <Box {...props} />,
            Pagination: (props: React.ComponentProps<typeof TablePagination>) => (
              <TablePagination {...props} style={{ paddingRight: '20px' }} />
            ),
          }}
          localization={localization(messages)}
          title={
            showTitle
              ? messages[intl.settings('all-customer-zone-subscriptions')]
              : ''
          }
          columns={columns(messages)}
          icons={icons}
          data={zones}
          actions={[
            {
              icon: icons.Add,
              tooltip: messages[intl.customerZones('zone-add-tooltip')],
              isFreeAction: true,
              onClick: () => {
                setIsAddOrEditZone(true)
                setEditableData(undefined)
              },
            },
            {
              icon: icons.Edit,
              tooltip: messages[intl.customerZones('zone-edit-tooltip')],
              onClick: (_event: React.MouseEvent, rowData: Zone | Zone[]) => {
                const data = Array.isArray(rowData) ? rowData[0] : rowData
                setIsAddOrEditZone(true)
                setEditableData(data)
              },
            },
            {
              icon: icons.Delete,
              tooltip: messages[intl.customerZones('zone-delete-tooltip')],
              onClick: (_event: React.MouseEvent, rowData: Zone | Zone[]) => {
                const data = Array.isArray(rowData) ? rowData[0] : rowData
                setDeleteData(data)
                setIsDeleteZone(true)
              },
            },
          ]}
        />
      </ThemeProvider>
    </Sidescroll>
  )
}

export default GlobalDataTable
